import React, { useEffect, useState } from 'react';
import {
  Input,
  InputGroup,
  Table,
  DOMHelper,
  Stack,
  Panel,
  IconButton,
  Pagination,
  Whisper,
  Tooltip,
  Message,
  useToaster
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import MoreIcon from '@rsuite/icons/legacy/More';
import { NameCell } from './Cells';
import { Edit, Icon } from '@rsuite/icons';
import { MdOutlineAddLink, MdOutlineDeleteOutline } from 'react-icons/md';
import { CiExport } from 'react-icons/ci';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import * as XLSX from 'xlsx';
import ConfirmModal from '@/components/ConfirmModal';
import { getWfhProfiles, removeWfhProfile } from '@/services/wfhProfile.service';
import EditDrawerView from './EditDrawerView';
import DrawerView from './DrawerView';
import { useTranslation } from 'react-i18next';
import { RiHomeOfficeLine } from 'react-icons/ri';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const toaster = useToaster();

  const [showDrawer, setShowDrawer] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [confirmModal, setConfirmModal] = useState<any>({
    open: false,
    title: '',
    subtitle: ''
  });

  const wfhProfiles = useAppSelector((state: any) => state.wfhProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);

  const dispatch = useAppDispatch();

  const loading = useAppSelector((state: any) => state.wfhProfile.loading);

  const [limit, setLimit] = React.useState(20);
  const [page, setPage] = React.useState(1);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getWfhProfiles());
    };

    fetchData();
  }, []);

  const data = wfhProfiles?.filter((v, i) => {
    const start = limit * (page - 1);
    const end = start + limit;
    return i >= start && i < end;
  });

  const handleSortColumn = (sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  const filteredData = () => {
    let filtered = data;
    if (searchKeyword?.length) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(item => {
        const matchesName = item.name.toLowerCase().includes(keyword);
        const matchesAuthorizedWfhDays = item.authorizedWfhDays
          ?.toString()
          .toLowerCase()
          .includes(keyword);
        const matchesDaysPerWeekWithBalance = item.daysPerWeekWithBalance
          ?.toString()
          .toLowerCase()
          .includes(keyword);
        const matchesDaysPerWeekWithoutBalance = item.daysPerWeekWithoutBalance
          ?.toString()
          .toLowerCase()
          .includes(keyword);

        return (
          matchesName ||
          matchesAuthorizedWfhDays ||
          matchesDaysPerWeekWithBalance ||
          matchesDaysPerWeekWithoutBalance
        );
      });
    }

    // Sort the filtered data if sortColumn and sortType are defined
    if (sortColumn && sortType) {
      filtered = filtered.sort((a, b) => {
        let x: any = a[sortColumn];
        let y: any = b[sortColumn];

        if (typeof x === 'string') x = x.toLowerCase(); // Ensure consistent comparison for strings
        if (typeof y === 'string') y = y.toLowerCase();

        if (sortType === 'asc') {
          return x < y ? -1 : x > y ? 1 : 0;
        } else {
          return x > y ? -1 : x < y ? 1 : 0;
        }
      });
    }

    return filtered;
  };

  const downloadExcel = (wfhProfileToExport, name) => {
    const worksheet = XLSX.utils.json_to_sheet(wfhProfileToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const wfhProfileToExport: any = [];
    filteredData().map((profile: any) => {
      wfhProfileToExport.push({
        name: profile.name,
        'Home office days': profile.authorizedWfhDays.join(', '),
        'NB days allowed /week with extra balance': profile.daysPerWeekWithBalance,
        'NB days allowed /week without extra balance': profile.daysPerWeekWithoutBalance
      });
    });
    downloadExcel(wfhProfileToExport, 'HomeOfficeProfiles');
  };
  const { t } = useTranslation();
  const handleDelete = async id => {
    try {
      dispatch(await removeWfhProfile(id))
        .then(() => {
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Deleted successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
        })
        .catch(e => {
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(e.message)}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setConfirmModal({ open: false });
  };
  const handleEdit = rowData => {
    setSelectedProfile(rowData);
    setShowEditDrawer(true);
  };
  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={setSearchKeyword} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>
        <Stack spacing={6}>
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>{t('Export')}</Tooltip>}
          >
            <IconButton
              // appearance="primary"
              icon={<Icon as={CiExport} />}
              onClick={handleExport}
            ></IconButton>
          </Whisper>
          <IconButton
            appearance="primary"
            icon={<Icon as={RiHomeOfficeLine} />}
            onClick={() => setShowDrawer(true)}
          >
            {t('Add home office profile')}
          </IconButton>
        </Stack>
      </Stack>

      <Panel bodyFill>
        <Table
          height={Math.max(getHeight(window) - 250, 400)}
          data={filteredData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={handleSortColumn}
          loading={loading}
        >
          <Column minWidth={250} flexGrow={1} sortable resizable>
            <HeaderCell>{t('Name')}</HeaderCell>
            <NameCell dataKey="name" />
          </Column>

          <Column width={330} sortable resizable>
            <HeaderCell>{t('Home office days')}</HeaderCell>
            <Cell>
              {rowData => {
                return <p>{rowData.authorizedWfhDays.join(', ')}</p>;
              }}
            </Cell>
          </Column>

          <Column width={330} sortable resizable>
            <HeaderCell>{t('NB days allowed /week with extra balance')}</HeaderCell>
            <Cell>
              {rowData => {
                return <p>{rowData.daysPerWeekWithBalance}</p>;
              }}
            </Cell>
          </Column>
          <Column width={330} sortable resizable>
            <HeaderCell>{t('NB days allowed /week without extra balance')}</HeaderCell>
            <Cell>
              {rowData => {
                return <p>{rowData.daysPerWeekWithoutBalance}</p>;
              }}
            </Cell>
          </Column>
          <Column width={50} flexGrow={0.5} fixed="right">
            <HeaderCell>
              <MoreIcon />
            </HeaderCell>
            {/* <ActionCell dataKey="id" /> */}
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack>
                  <IconButton
                    onClick={() => handleEdit(rowData)}
                    size="xs"
                    icon={<Edit color="blue" />}
                    id={`edit-${rowData._id}`}
                  />

                  <IconButton
                    size="xs"
                    icon={<MdOutlineDeleteOutline color="red" />}
                    onClick={() => {
                      setConfirmModal({
                        open: true,
                        title: 'Are you sure to remove this home office profile ?',
                        subtitle: 'You cannot undo this operation',
                        onConfirm: () => {
                          handleDelete(rowData._id);
                        }
                      });
                    }}
                    id={`delete-${rowData._id}`}
                  />
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={wfhProfiles?.length ? wfhProfiles?.length : 0}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </Panel>

      <DrawerView open={showDrawer} onClose={() => setShowDrawer(false)} />
      {showEditDrawer && (
        <EditDrawerView
          open={showEditDrawer}
          profile={selectedProfile}
          onClose={() => setShowEditDrawer(false)}
        />
      )}

      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModal} />
      )}
    </>
  );
};

export default DataTable;
